import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useLang } from "@/i18n/LanguageContext";
import { site } from "@/config/site";

export const ContactSection = () => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast({ title: t("form.sent") });
      (e.target as HTMLFormElement).reset();
    }, 600);
  };

  return (
    <section id="kontakt" className="py-24 bg-gradient-hero text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,hsl(var(--accent)/0.18),transparent_60%)]" />
      <div className="container relative grid lg:grid-cols-2 gap-12">
        <div>
          <span className="text-accent-on-dark font-semibold uppercase tracking-widest text-sm">{t("nav.contact")}</span>
          <h2 className="font-display text-4xl md:text-6xl mt-3 mb-6">{t("section.contact.title")}</h2>
          <p className="text-primary-foreground/80 text-lg mb-8 max-w-md">{t("section.contact.subtitle")}</p>

          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <Button variant="emergency" size="xl" asChild>
              <a href={site.phoneHref} className="gap-3"><Phone className="!w-5 !h-5" /> {site.phoneDisplay}</a>
            </Button>
            <Button variant="outlineLight" size="xl" asChild>
              <a href={site.whatsappHref} target="_blank" rel="noopener" className="gap-3"><MessageCircle className="!w-5 !h-5" /> WhatsApp</a>
            </Button>
          </div>

          <div className="space-y-4">
            {[
              { icon: Mail, label: "E-Mail", value: site.email },
              { icon: MapPin, label: "Adresse", value: site.address },
              { icon: Clock, label: "Öffnungszeiten", value: "24 / 7 / 365" },
            ].map((c) => (
              <div key={c.label} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent-on-dark/15 border border-accent-on-dark/30 grid place-items-center shrink-0">
                  <c.icon className="w-5 h-5 text-accent-on-dark" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-primary-foreground/60">{c.label}</div>
                  <div className="font-medium">{c.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-primary-foreground/5 backdrop-blur-sm border border-primary-foreground/10 rounded-2xl p-7 md:p-8 shadow-elegant space-y-4">
          <h3 className="font-display text-3xl tracking-wide">{t("cta.freeQuote")}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input required placeholder={t("form.name")} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 h-12" />
            <Input required type="tel" placeholder={t("form.phone")} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 h-12" />
          </div>
          <Input placeholder={t("form.address")} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 h-12" />
          <Textarea required placeholder={t("form.message")} className="bg-primary-foreground/5 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 min-h-[120px]" />
          <Button type="submit" variant="hero" size="xl" className="w-full" disabled={loading}>
            {loading ? "..." : t("form.submit")}
          </Button>
        </form>
      </div>
    </section>
  );
};